/**
 * Upload / persist standalone PDF documents to Firebase Storage + Firestore.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  Timestamp,
} from 'firebase/firestore';
import {
  getBytes,
  getDownloadURL,
  ref,
  uploadBytes,
  deleteObject,
} from 'firebase/storage';
import type { PdfDocumentMeta, PdfPageAnnotations } from '@canvas/shared-types';
import {
  pdfDocumentPath,
  pdfDocumentPagePath,
  pdfDocumentPagesPath,
  userPdfDocumentsPath,
} from '@canvas/shared-types';
import { STORAGE_PATHS } from '@canvas/firebase-config/config';
import { getClientAuth, getClientDB, getClientStorage } from '@canvas/firebase-config/client';
import {
  idbDeletePdfSource,
  idbGetPdfSource,
  idbPutPdfSource,
} from '@/lib/pdf/pdfSourceIdb';
import { decodeFirestoreValue, encodeFirestoreValue } from '@/lib/firestoreSafe';

const MAX_PDF_BYTES = 50 * 1024 * 1024; // 50 MB soft client limit
const DOWNLOAD_TIMEOUT_MS = 12_000;

function path(segments: string) {
  return segments.replace(/^\/+/, '');
}

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return new Date();
}

function copyToArrayBuffer(src: ArrayBuffer): ArrayBuffer {
  const copy = new Uint8Array(src.byteLength);
  copy.set(new Uint8Array(src));
  return copy.buffer;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`));
    }, ms);
    promise.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (err) => {
        window.clearTimeout(t);
        reject(err);
      }
    );
  });
}

export function normalizePdfMeta(
  data: Record<string, unknown>,
  fallbackId: string
): PdfDocumentMeta {
  return {
    docId: (data.docId as string) || fallbackId,
    userId: (data.userId as string) || '',
    name: (data.name as string) || 'Untitled.pdf',
    storagePath: (data.storagePath as string) || '',
    pageCount: Number(data.pageCount) || 0,
    thumbnailUrl: data.thumbnailUrl as string | undefined,
    fileSize: Number(data.fileSize) || 0,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

export async function listPdfDocuments(userId: string): Promise<PdfDocumentMeta[]> {
  const db = getClientDB();
  const snap = await getDocs(collection(db, path(userPdfDocumentsPath(userId))));
  return snap.docs
    .map((d) => normalizePdfMeta(d.data(), d.id))
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
}

export async function uploadPdfDocument(options: {
  bytes: ArrayBuffer;
  name: string;
  pageCount: number;
  thumbnailDataUrl?: string;
  docId?: string;
}): Promise<PdfDocumentMeta> {
  const auth = getClientAuth();
  const userId = auth.currentUser?.uid;
  if (!userId) throw new Error('Sign in to save PDFs');

  if (options.bytes.byteLength > MAX_PDF_BYTES) {
    throw new Error('PDF must be 50 MB or smaller');
  }

  const docId = options.docId || crypto.randomUUID();
  const storagePath = STORAGE_PATHS.pdfDocuments(userId, docId);
  const storage = getClientStorage();
  const storageRef = ref(storage, storagePath);
  let pdfBytes: Uint8Array;
  try {
    pdfBytes = new Uint8Array(options.bytes.byteLength);
    pdfBytes.set(new Uint8Array(options.bytes));
  } catch {
    throw new Error(
      'PDF source data is no longer available. Close and reopen the PDF, then try again.'
    );
  }
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });

  await uploadBytes(storageRef, blob, {
    contentType: 'application/pdf',
    customMetadata: { originalName: options.name.slice(0, 120) },
  });

  let thumbnailUrl: string | undefined;
  if (options.thumbnailDataUrl?.startsWith('data:')) {
    try {
      const thumbPath = STORAGE_PATHS.pdfDocumentThumb(userId, docId);
      const res = await fetch(options.thumbnailDataUrl);
      const thumbBlob = await res.blob();
      const thumbRef = ref(storage, thumbPath);
      await uploadBytes(thumbRef, thumbBlob, { contentType: 'image/jpeg' });
      thumbnailUrl = await getDownloadURL(thumbRef);
    } catch (err) {
      console.warn('PDF thumb upload failed', err);
    }
  }

  const now = new Date();
  const meta: PdfDocumentMeta = {
    docId,
    userId,
    name: options.name,
    storagePath,
    pageCount: options.pageCount,
    thumbnailUrl,
    fileSize: options.bytes.byteLength,
    createdAt: now,
    updatedAt: now,
  };

  const db = getClientDB();
  await setDoc(doc(db, path(pdfDocumentPath(userId, docId))), {
    ...meta,
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now),
  });

  // Local cache so reopen works even if Storage CORS blocks browser downloads
  try {
    await idbPutPdfSource(docId, options.bytes);
  } catch (err) {
    console.warn('PDF source IDB cache failed', err);
  }

  return meta;
}

export async function savePdfPageAnnotations(options: {
  docId: string;
  pageIndex: number;
  fabricJson: Record<string, unknown>;
}): Promise<void> {
  const auth = getClientAuth();
  const userId = auth.currentUser?.uid;
  if (!userId) return;

  const db = getClientDB();
  const now = new Date();
  const payload: PdfPageAnnotations = {
    pageIndex: options.pageIndex,
    fabricJson: encodeFirestoreValue(options.fabricJson) as Record<string, unknown>,
    updatedAt: now,
  };

  await setDoc(
    doc(db, path(pdfDocumentPagePath(userId, options.docId, options.pageIndex))),
    {
      ...payload,
      updatedAt: Timestamp.fromDate(now),
    }
  );

  await setDoc(
    doc(db, path(pdfDocumentPath(userId, options.docId))),
    { updatedAt: Timestamp.fromDate(now) },
    { merge: true }
  );
}

export async function loadPdfPageAnnotations(
  docId: string,
  pageIndex: number
): Promise<Record<string, unknown> | null> {
  const auth = getClientAuth();
  const userId = auth.currentUser?.uid;
  if (!userId) return null;

  const db = getClientDB();
  const snap = await getDoc(
    doc(db, path(pdfDocumentPagePath(userId, docId, pageIndex)))
  );
  if (!snap.exists()) return null;
  const data = snap.data();
  const raw = data.fabricJson as Record<string, unknown> | undefined;
  if (!raw) return null;
  return decodeFirestoreValue(raw) as Record<string, unknown>;
}

export async function loadAllPdfPageAnnotations(
  docId: string
): Promise<Record<number, Record<string, unknown>>> {
  const auth = getClientAuth();
  const userId = auth.currentUser?.uid;
  if (!userId) return {};

  const db = getClientDB();
  const snap = await getDocs(
    collection(db, path(pdfDocumentPagesPath(userId, docId)))
  );
  const out: Record<number, Record<string, unknown>> = {};
  for (const d of snap.docs) {
    const data = d.data();
    const idx = Number(data.pageIndex ?? d.id);
    if (Number.isFinite(idx) && data.fabricJson) {
      out[idx] = decodeFirestoreValue(data.fabricJson) as Record<string, unknown>;
    }
  }
  return out;
}

/**
 * Download PDF bytes from Storage using authenticated REST first
 * (avoids CORS / hanging getBytes issues on some setups).
 */
async function downloadViaAuthRest(storagePath: string): Promise<ArrayBuffer> {
  const auth = getClientAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Sign in to open saved PDFs');

  const storage = getClientStorage();
  const bucket = storage.app.options.storageBucket;
  if (!bucket) throw new Error('Storage bucket is not configured');

  const token = await user.getIdToken();
  const encoded = encodeURIComponent(storagePath);
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encoded}?alt=media`;

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (res.status === 404) {
    throw new Error('PDF file was not found in storage. It may have been deleted.');
  }
  if (res.status === 403) {
    throw new Error('You do not have permission to open this PDF. Deploy Storage rules for pdf/.');
  }
  if (!res.ok) {
    throw new Error(`Failed to download PDF (${res.status})`);
  }

  return copyToArrayBuffer(await res.arrayBuffer());
}

async function downloadViaGetBytes(storagePath: string): Promise<ArrayBuffer> {
  const storage = getClientStorage();
  const bytes = await getBytes(ref(storage, storagePath));
  return copyToArrayBuffer(bytes);
}

async function downloadViaDownloadUrl(storagePath: string): Promise<ArrayBuffer> {
  const storage = getClientStorage();
  const url = await getDownloadURL(ref(storage, storagePath));
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to download PDF (${res.status})`);
  return copyToArrayBuffer(await res.arrayBuffer());
}

export async function fetchPdfBytesFromStorage(
  storagePath: string,
  docId?: string
): Promise<ArrayBuffer> {
  // Prefer local cache (avoids Storage CORS "Failed to fetch" in the browser)
  if (docId) {
    try {
      const cached = await idbGetPdfSource(docId);
      if (cached && cached.byteLength > 0) {
        return cached;
      }
    } catch (err) {
      console.warn('PDF IDB cache read failed', err);
    }
  }

  if (!storagePath?.trim()) {
    throw new Error('This PDF has no storage path. Re-open the original file and save again.');
  }

  const errors: string[] = [];
  let bytes: ArrayBuffer | null = null;

  // Tokenized download URL first (often works when Authorization header triggers CORS)
  try {
    bytes = await withTimeout(
      downloadViaDownloadUrl(storagePath),
      DOWNLOAD_TIMEOUT_MS,
      'PDF download (URL)'
    );
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.warn('PDF download via download URL failed:', msg);
    errors.push(msg);
  }

  if (!bytes) {
    try {
      bytes = await withTimeout(
        downloadViaGetBytes(storagePath),
        DOWNLOAD_TIMEOUT_MS,
        'PDF download (SDK)'
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('PDF download via getBytes failed:', msg);
      errors.push(msg);
    }
  }

  if (!bytes) {
    try {
      bytes = await withTimeout(
        downloadViaAuthRest(storagePath),
        DOWNLOAD_TIMEOUT_MS,
        'PDF download'
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('PDF download via auth REST failed:', msg);
      errors.push(msg);
    }
  }

  if (bytes && bytes.byteLength > 0) {
    if (docId) {
      void idbPutPdfSource(docId, bytes).catch(() => undefined);
    }
    return bytes;
  }

  const joined = errors.filter(Boolean).join(' · ');
  if (/object-not-found|not found|404/i.test(joined)) {
    throw new Error('PDF file was not found in storage. It may have been deleted.');
  }
  if (/unauthorized|permission|403/i.test(joined)) {
    throw new Error('You do not have permission to open this PDF.');
  }
  if (/failed to fetch|timed out|cors|network/i.test(joined)) {
    throw new Error(
      'Browser blocked downloading this PDF from Storage (CORS). Choose the original PDF file once to restore access.'
    );
  }
  throw new Error(joined || 'Failed to download PDF');
}

export async function deletePdfDocument(userId: string, meta: PdfDocumentMeta): Promise<void> {
  const db = getClientDB();
  const storage = getClientStorage();

  try {
    if (meta.storagePath) {
      await deleteObject(ref(storage, meta.storagePath));
    }
  } catch {
    /* ignore missing object */
  }

  void idbDeletePdfSource(meta.docId);

  const pagesSnap = await getDocs(
    collection(db, path(`${pdfDocumentPath(userId, meta.docId)}/pages`))
  );
  await Promise.all(pagesSnap.docs.map((d) => deleteDoc(d.ref)));
  await deleteDoc(doc(db, path(pdfDocumentPath(userId, meta.docId))));
}
